import React, {
    useMemo,
    useState,
} from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import DataTableImport, {
    Alignment,
    Direction,
} from 'react-data-table-component'
import type { TableColumn } from 'react-data-table-component'
// react-data-table-component ships a default export that may be wrapped —
// handle both the plain and `.default`-wrapped module shapes.
const DataTable = (DataTableImport as unknown as { default: typeof DataTableImport }).default || DataTableImport
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
} from 'reactstrap'
import { Certificates } from '../parts/certificates'
import { deleteDevice } from '../helpers'
import {
    ACTIVE_STATUS,
    VOLTAGE,
} from '../../../components/shared/device-addresses'
import {
    alarmsActive,
    isServiceInProgress,
} from '../detail/parse-device-data'
import { getMapMarkerIcon } from '../detail/mapMarkers'
import { EditDevice } from '../forms/edit-device'
import { normalizeStringElement } from '../../../components/shared/search-substring'
import type { RootState } from '../../../store'
import type { Device, DeviceDataItem } from '../../../types/domain'
import type { DeviceEntity } from '../detail/parse-device-data'

/** Minimal device shape required by this table */
export interface DeviceRow {
    uid?: string
    name: string
    ipAddress?: string
    isMonitored?: boolean
    lastSeenStatus?: string
    oilLevelPercentage?: number
    oilAlarm?: boolean
    locationIP?: string
    caPath?: string
    readData?: DeviceDataItem[]
    [key: string]: unknown
}

interface RowActionsDropdownProps {
    children: React.ReactNode
}

const RowActionsDropdown = ({ children }: RowActionsDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false)
    return (
        <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)}>
            {children}
        </Dropdown>
    )
}

interface DevicesTableProps {
    /** Callback to reload the device list after a mutating action */
    reloadData: () => void
    /** Override the Redux devices list with a pre-filtered array */
    devices?: DeviceRow[]
    /** Current page (1-based) */
    page: number
    /** Setter for the current page */
    setPage: (page: number) => void
    /** Rows per page */
    perPage: number
    /** Setter for rows per page (and active page) */
    setPerPage: (perPage: number, page: number) => void
    /**
     * Current sort. One of: `name` (preset, A-Z), `status` (preset,
     * active → availability → oil level → name), or `<column>-<asc|desc>`.
     */
    sort: string
    /** Setter for the sort value (writes through to URL) */
    setSort: (sort: string) => void
    /** Base64-encoded backlink passed to device detail links */
    backlink: string
}

/** Multi-key default sort: active first, then available, then lowest oil, then name. */
const compareByStatus = (itemA: DeviceRow, itemB: DeviceRow): number => {
    const isDeviceActive = (row: DeviceRow): boolean => {
        const hint = row?.readData?.find?.(h => (h as { hint?: string }).hint === ACTIVE_STATUS)
        const data = hint ? (hint as { data?: unknown }).data : null
        return data !== null && data !== undefined && parseInt(String(data), 10) === 0
    }

    const aActive = isDeviceActive(itemA) ? 0 : 1
    const bActive = isDeviceActive(itemB) ? 0 : 1
    if (aActive !== bActive) {
        return aActive - bActive
    }

    const aAvailable = itemA.lastSeenStatus === 'ok' ? 0 : 1
    const bAvailable = itemB.lastSeenStatus === 'ok' ? 0 : 1
    if (aAvailable !== bAvailable) {
        return aAvailable - bAvailable
    }

    const aOil = itemA.oilLevelPercentage ?? Number.POSITIVE_INFINITY
    const bOil = itemB.oilLevelPercentage ?? Number.POSITIVE_INFINITY
    if (aOil !== bOil) {
        return aOil - bOil
    }

    return (itemA.name ?? '').trim().localeCompare((itemB.name ?? '').trim(), 'cs')
}

/**
 * Alphabetical (cs) compare on the `name` field.
 *
 * Trim leading/trailing whitespace so devices whose name was entered with an
 * accidental leading space don't get pinned to the top of the list (space has
 * a lower code point than letters and would otherwise win the comparison).
 */
const compareByName = (itemA: DeviceRow, itemB: DeviceRow): number =>
    (itemA.name ?? '').trim().localeCompare((itemB.name ?? '').trim(), 'cs')

/** Column-header compare for any field; uses the same normalisation as before. */
const compareByColumn = (
    field: string,
    order: 'asc' | 'desc',
): ((itemA: DeviceRow, itemB: DeviceRow) => number) => (itemA, itemB) => {
    const itemANorm = normalizeStringElement(String(itemA?.[field]))
    const itemBNorm = normalizeStringElement(String(itemB?.[field]))
    if (itemANorm === itemBNorm) {
        return 0
    }
    if (order === 'asc') {
        return (itemANorm ?? '') < (itemBNorm ?? '') ? -1 : 1
    }
    return (itemBNorm ?? '') < (itemANorm ?? '') ? -1 : 1
}

const resolveComparator = (sort: string): (itemA: DeviceRow, itemB: DeviceRow) => number => {
    if (sort === 'status') {
        return compareByStatus
    }
    if (sort === 'name' || !sort) {
        return compareByName
    }
    const [field, order] = sort.split('-')
    if (field && (order === 'asc' || order === 'desc')) {
        return compareByColumn(field, order)
    }
    return compareByName
}

/**
 * Data table for the devices list page.
 *
 * Handles client-side sorting and slice-based pagination.
 * Each row exposes edit and delete actions.
 */
const DevicesTable = ({
    reloadData,
    devices: devicesProps,
    page,
    setPage,
    perPage,
    setPerPage,
    sort,
    setSort,
    backlink,
}: DevicesTableProps) => {
    const reduxDevices = useSelector((state: RootState) => state.devices.data) as DeviceRow[]
    const devices: DeviceRow[] = devicesProps || reduxDevices
    const { t } = useTranslation()

    const sortedDevices = useMemo<DeviceRow[]>(() => {
        const comparator = resolveComparator(sort)
        const sortedAll = [...devices].sort(comparator)
        const startIndex = (page - 1) * perPage
        return sortedAll.slice(startIndex, startIndex + perPage)
    }, [
        devices,
        sort,
        page,
        perPage,
    ])

    /**
     * Check if device is monitored
     */
    const monitoringFormatter = (row: DeviceRow): React.ReactNode => (
        row.isMonitored ? (
            <i className="fas fa-check text-success" />
        ) : (
            <i className="fas fa-times text-gray-700" />
        )
    )

    /**
     * Check if device last state is ok
     */
    const lastStateFormatter = (row: DeviceRow): React.ReactNode => (
        row.lastSeenStatus === 'ok' ? (
            <i className='fas fa-check text-success'/>
        ) : (
            <i className='fas fa-times text-gray-700'/>
        )
    )

    /**
     * Find device name by uid and format it to a link
     */
    const nameFormatter = (row: DeviceRow): React.ReactNode => {
        const deviceName = row.name
        return <Link to={`/devices/${row.uid}?backlink=${backlink}`} className="text-900">{deviceName}</Link>
    }

    /**
     * Display oil level percentage
     */
    const oilLevelPercentageFormatter = (row: DeviceRow): React.ReactNode => {
        const icon = (
            <img
                src={getMapMarkerIcon(row.oilLevelPercentage, row.isMonitored, false)}
                alt=""
                width="30"
            />
        )

        return (
            <div className="d-flex flex-row align-items-center">
                {icon}
                <span className="ml-n1">
                    {Math.floor(row.oilLevelPercentage ?? 0)} %
                </span>
            </div>
        )
    }

    /**
     * Display voltage
     */
    const voltageFormatter = (row: DeviceRow): React.ReactNode => {
        const voltageHint = row?.readData?.find?.(hint => (hint as { hint?: string }).hint === VOLTAGE)
        if (voltageHint) {
            return <> {Number((voltageHint as { data?: unknown }).data) / 1000} V</>
        }

        return <i className="fas fa-minus text-500"/>
    }

    /**
     * Display alarm state, with an informational (non-alarm) service indicator next to it
     */
    const alarmsFormatter = (row: DeviceRow): React.ReactNode => {
        const isAlarmsActive = alarmsActive(row as DeviceEntity)
        const serviceInProgress = isServiceInProgress(row as DeviceEntity)

        return (
            <div className="d-flex flex-row align-items-center">
                {isAlarmsActive
                    ? <i className="fas fa-exclamation-triangle text-danger"/>
                    : <i className="fas fa-check text-success"/>}
                {serviceInProgress && (
                    <i
                        className="fas fa-tools text-info ml-2"
                        title={t('deviceDetail.serviceMode')}
                    />
                )}
            </div>
        )
    }

    /**
     * Display dropdown with actions available for each row
     */
    const actionFormatter = (row: DeviceRow): React.ReactNode => {
        return (
            <div className="d-flex flex-row align-items-center justify-content-end">
                <EditDevice
                    updatedDevice={row}
                    onModalClose={reloadData}
                >
                    <button className="btn btn-outline-light">
                        <i className="fas fa-pencil-alt mr-2 text-500"/>
                    </button>
                </EditDevice>
                <RowActionsDropdown>
                    <DropdownToggle color="link" size="sm" className="text-600 btn-reveal mr-3">
                        <FontAwesomeIcon icon="ellipsis-h" className="fs--1"/>
                    </DropdownToggle>
                    <DropdownMenu positionFixed={true} right className="border py-2">
                        <DropdownItem
                            onClick={() => window.swconfirm({
                                title: t('device.deleteDevicePrompt', { deviceName: row.name }),
                                onConfirm: () => deleteDevice(row as Device),
                            })}
                        >
                            <i className="fas fa-trash mr-2 text-500"/>
                            {t('device.deleteDevice')}
                        </DropdownItem>
                        <DropdownItem divider/>
                        <DropdownItem>
                            <Certificates
                                device={row}
                            />
                        </DropdownItem>
                    </DropdownMenu>
                </RowActionsDropdown>
            </div>
        )
    }

    const columns: TableColumn<DeviceRow>[] = [
        {
            id: 'name',
            name: 'Název',
            selector: row => row.name ?? '',
            cell: nameFormatter,
            sortable: true,
            minWidth: '500px',
            // Custom field identifier used by sortServer handler
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(({ element: 'name' } as any)),
        },
        {
            id: 'ipAddress',
            name: 'IP',
            selector: row => row.ipAddress ?? '',
            sortable: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(({ element: 'ipAddress' } as any)),
        },
        {
            id: 'oilLevelPercentage',
            name: 'Mazivo',
            selector: row => row.oilLevelPercentage ?? 0,
            cell: oilLevelPercentageFormatter,
            center: true,
            sortable: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(({ element: 'oilLevelPercentage' } as any)),
        },
        {
            name: 'Napětí',
            selector: row => {
                const hint = row?.readData?.find?.(h => (h as { hint?: string }).hint === VOLTAGE)
                return hint ? Number((hint as { data?: unknown }).data) : 0
            },
            cell: voltageFormatter,
            center: true,
            sortable: false,
        },
        {
            name: 'Alarmy',
            selector: row => String(alarmsActive(row as DeviceEntity)),
            cell: alarmsFormatter,
            sortable: false,
            center: true,
        },
        {
            id: 'lastSeenStatus',
            name: 'Dostupnost',
            selector: row => row.lastSeenStatus ?? '',
            cell: lastStateFormatter,
            sortable: true,
            center: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(({ element: 'lastSeenStatus' } as any)),
        },
        {
            id: 'isMonitored',
            name: 'Monitorovaná',
            selector: row => String(row.isMonitored ?? false),
            cell: monitoringFormatter,
            sortable: true,
            center: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(({ element: 'isMonitored' } as any)),
        },
        {
            name: '',
            cell: actionFormatter,
            right: true,
        },
    ]

    const sortParts = sort.includes('-') ? sort.split('-') : []
    const defaultSortFieldId = sortParts[0] || undefined
    const defaultSortAsc = sortParts[1] !== 'desc'

    return (
        <div className="table-responsive">
            <DataTable
                data={sortedDevices}
                columns={columns}
                direction={Direction.AUTO}
                fixedHeaderScrollHeight="300px"
                pagination
                responsive
                subHeaderAlign={Alignment.RIGHT}
                subHeaderWrap
                paginationServer
                paginationDefaultPage={page}
                paginationTotalRows={devices.length}
                paginationPerPage={perPage}
                onChangePage={setPage}
                onChangeRowsPerPage={(newPerPage: number, newPage: number) => {
                    setPerPage(newPerPage, newPage)
                }}
                sortServer
                defaultSortFieldId={defaultSortFieldId}
                defaultSortAsc={defaultSortAsc}
                onSort={(column: TableColumn<DeviceRow> & { element?: string }, sortOrder: string) => {
                    if (column.element) {
                        setSort(`${column.element}-${sortOrder}`)
                    }
                }}
            />
        </div>
    )
}

export default DevicesTable
